import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { dealSchema, type DealFormData } from '@/lib/validators';
import { useDeal, useDeals } from '@/composables/crm/useDeals';
import { usePipelines } from '@/composables/crm/usePipeline';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { CURRENCIES } from '@/lib/constants';

export function DealCreatePage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { createDeal, updateDeal } = useDeals();
  const { deal, isLoading } = useDeal(id || '');
  const { pipelines } = usePipelines();

  const form = useForm<DealFormData>({ resolver: zodResolver(dealSchema), defaultValues: { title: '', value: 0, currency: 'USD', description: '', pipelineId: '', stageId: '' } });

  useEffect(() => {
    if (deal && isEdit) {
      form.reset({
        title: deal.title || '',
        value: deal.value ?? 0,
        currency: (deal.currency as DealFormData['currency']) || 'USD',
        description: deal.description || '',
        pipelineId: deal.pipelineId || deal.pipeline?.id || '',
        stageId: deal.stageId || deal.stage?.id || '',
        expectedCloseDate: deal.expectedCloseDate || '',
      });
    }
  }, [deal, isEdit, form]);

  // Pre-select default pipeline (or first available) for new deals
  useEffect(() => {
    if (!isEdit && pipelines.length > 0 && !form.getValues('pipelineId')) {
      const defaultPipeline = pipelines.find((p) => p.isDefault) || pipelines[0];
      form.setValue('pipelineId', defaultPipeline.id);
      if (defaultPipeline.stages?.length) {
        form.setValue('stageId', defaultPipeline.stages[0].id);
      }
    }
  }, [isEdit, pipelines, form]);

  const selectedPipelineId = form.watch('pipelineId');
  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId);
  const stageOptions = (selectedPipeline?.stages || []).map((s) => ({ label: s.name, value: s.id }));

  const onSubmit = (data: DealFormData) => {
    if (isEdit) {
      updateDeal.mutate({ id, data }, { onSuccess: () => navigate(`/deals/${id}`) });
    } else {
      createDeal.mutate(data, { onSuccess: () => navigate('/deals/pipeline') });
    }
  };

  if (isEdit && isLoading) return <LoadingSpinner />;

  const isPending = isEdit ? updateDeal.isPending : createDeal.isPending;

  return (
    <PageContainer backTo title={isEdit ? 'Edit Deal' : 'Create Deal'}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card><CardHeader><CardTitle className="text-base">Deal Info</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormField form={form} name="title" label="Deal Title" required /><FormCurrencyInput form={form} name="value" label="Deal Value" required /><FormSelect form={form} name="currency" label="Currency" options={CURRENCIES.map(c => ({ label: c, value: c }))} /><FormTextarea form={form} name="description" label="Description" /><FormDatePicker form={form} name="expectedCloseDate" label="Expected Close Date" />
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Pipeline & Stage</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormSelect form={form} name="pipelineId" label="Pipeline" required options={pipelines.map(p => ({ label: p.name, value: p.id }))} /><FormSelect form={form} name="stageId" label="Stage" required options={stageOptions} />
          </CardContent></Card>
        </div>
        <div className="mt-6 flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button><Button type="submit" disabled={isPending}>{isPending ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Deal')}</Button></div>
      </form>
    </PageContainer>
  );
}
